import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendarAlt, FaInfo, FaShieldAlt, FaQuestion, FaChartPie, FaCalculator, FaChartArea, FaBuilding, FaWallet, FaPiggyBank, FaMoneyBillWave, FaBank, FaClock } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const RDCalculator = () => {
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('12');
  const [interestRate, setInterestRate] = useState('7.25');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateRD = () => {
    if (!monthlyContribution || !investmentPeriod || !interestRate) {
      setError('Please fill in all required fields');
      return;
    }

    const P = parseFloat(monthlyContribution);
    const n = parseFloat(investmentPeriod);
    const r = parseFloat(interestRate) / 100 / 12; // Monthly interest rate

    if (isNaN(P) || isNaN(n) || isNaN(r)) {
      setError('Please enter valid numbers');
      return;
    }

    if (n < 6 || n > 60) {
      setError('Investment period must be between 6 months and 5 years');
      return;
    }

    if (r <= 0 || r >= 1) {
      setError('Please enter a valid interest rate');
      return;
    }

    // Calculate maturity amount using RD formula
    const totalMonths = n * 12;
    let maturityAmount = 0;

    for (let month = 1; month <= totalMonths; month++) {
      maturityAmount += P * Math.pow((1 + r), (totalMonths - month + 1));
    }

    const totalInvestment = P * totalMonths;
    const totalInterest = maturityAmount - totalInvestment;

    const resultData = {
      totalInvestment: totalInvestment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      maturityAmount: maturityAmount.toFixed(2),
      interestRate: parseFloat(interestRate)
    };

    setResult(resultData);
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const chartOptions = {
    chart: {
      type: 'donut',
      height: 350,
      toolbar: {
        show: false
      }
    },
    colors: ['#6366F1', '#10B981'],
    labels: ['Principal Amount', 'Interest Earned'],
    legend: {
      position: 'bottom',
      fontSize: '16px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: '#1F2937',
        useSeriesColors: true
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '16px',
        fontFamily: 'Inter, sans-serif',
        colors: ['#1F2937']
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '22px',
              fontFamily: 'Inter, sans-serif',
              color: '#1F2937',
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '26px',
              fontFamily: 'Inter, sans-serif',
              color: '#1F2937',
              offsetY: 16
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '22px',
              fontFamily: 'Inter, sans-serif',
              color: '#1F2937'
            }
          }
        }
      }
    }
  };

  const chartSeries = result ? [parseFloat(result.totalInvestment), parseFloat(result.totalInterest)] : [];

  return (
    <div className="bg-gradient-to-br from-purple-50 to-white min-h-screen p-8">
      <Helmet>
        <title>RD Calculator</title>
        <meta name="description" content="Calculate your Recurring Deposit (RD) returns and maturity amount." />
        <meta property="og:title" content="RD Calculator" />
        <meta property="og:description" content="Calculate your Recurring Deposit (RD) returns and maturity amount." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaBuilding className="mr-3 text-4xl text-purple-600" />
            RD Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Current Interest Rate:</span>
            <span className="px-4 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
              {interestRate}%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaWallet className="inline-block mr-2 text-purple-500" />
              Monthly Contribution (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" />
              <input
                type="number"
                value={monthlyContribution}
                onChange={(e) => setMonthlyContribution(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
                placeholder="Enter monthly contribution"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaClock className="inline-block mr-2 text-purple-500" />
              Investment Period (Months)
            </label>
            <select
              value={investmentPeriod}
              onChange={(e) => setInvestmentPeriod(e.target.value)}
              className="w-full p-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
            >
              <option value="6">6 Months</option>
              <option value="9">9 Months</option>
              <option value="12">12 Months</option>
              <option value="18">18 Months</option>
              <option value="24">24 Months</option>
              <option value="36">36 Months</option>
              <option value="48">48 Months</option>
              <option value="60">60 Months</option>
            </select>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaChartLine className="inline-block mr-2 text-purple-500" />
              Interest Rate (%)
            </label>
            <input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full p-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
              placeholder="Enter interest rate"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <button
          onClick={calculateRD}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-black font-bold py-6 px-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10 border-4 border-purple-500 flex items-center justify-center gap-3"
        >
          <FaCalculator className="inline-block text-2xl" />
          <span>Calculate RD Returns</span>
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartPie className="mr-3 text-purple-600" />
              Investment Breakdown
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={chartOptions} series={chartSeries} type="donut" height={350} />
            </div>
            <div className="mt-6 p-6 bg-purple-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Breakdown Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Investment</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(result.totalInvestment)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Interest Earned</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(result.totalInterest)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Maturity Amount</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(result.maturityAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-purple-600" />
            About RD
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-purple-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">Key Features</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Monthly fixed deposits</li>
                <li>Minimum investment of ₹500</li>
                <li>Flexible tenure (6-60 months)</li>
                <li>Higher interest rates than FD</li>
              </ul>
            </div>
            <div className="p-6 bg-purple-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Disciplined savings habit</li>
                <li>Higher returns than regular savings</li>
                <li>Tax benefits under Section 80C</li>
                <li>Flexible investment options</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestion className="mr-3 text-purple-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">What is Recurring Deposit?</h3>
              <p className="text-gray-600">
                Recurring Deposit (RD) is a savings scheme that allows you to deposit a fixed amount monthly at a fixed interest rate.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">What is the minimum amount?</h3>
              <p className="text-gray-600">
                The minimum monthly deposit amount is ₹500.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Is RD tax-free?</h3>
              <p className="text-gray-600">
                Interest earned is taxable, but investments qualify for tax benefits under Section 80C.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Can I withdraw before maturity?</h3>
              <p className="text-gray-600">
                Premature withdrawal is allowed with a penalty, and interest is calculated at the premature withdrawal rate.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-purple-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 1: Enter Monthly Contribution</h3>
              <p className="text-gray-600">
                Input the monthly amount you wish to invest in RD.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 2: Select Investment Period</h3>
              <p className="text-gray-600">
                Choose your preferred investment period (6-60 months).
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 3: Enter Interest Rate</h3>
              <p className="text-gray-600">
                Enter the current interest rate offered by your bank.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 4: Calculate Returns</h3>
              <p className="text-gray-600">
                Click the "Calculate RD Returns" button to see your maturity amount and total returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RDCalculator;
